import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  AuditOverview, EngagementDetail, EvidenceRequest, ScopePreviewRow,
} from '@/api/types/cts-audit.types';

// ── TC5 — the Audit Center ───────────────────────────────────────────────────────────────────

export function useAuditOverview() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-audit', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/audit');
      return data.data as AuditOverview;
    },
    enabled: !!tenantId,
  });
}

/** One engagement: its summary and the controls in its frozen scope. */
export function useEngagement(tokenId: string | undefined) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-engagement', tenantId, tokenId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/workspace/audit/engagements/${tokenId}`);
      return data.data as EngagementDetail;
    },
    enabled: !!tenantId && !!tokenId,
  });
}

/** The scope BUILDER's per-framework numbers, over the entitled catalogue only. */
export function useScopePreview(enabled = true) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-scope-preview', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/audit/scope-preview');
      return data.data as ScopePreviewRow[];
    },
    enabled: !!tenantId && enabled,
  });
}

/**
 * ⚠ SENDS THE FRAMEWORKS, NOT THE CONTROLS. The server resolves and freezes; a client-supplied
 * control list is never the answer, so a stale page cannot widen a grant.
 */
export function useGrantAuditorScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { tokenId: string; frameworkReferences: string[]; controlReferences?: string[] }) => {
      const { data } = await apiClient.post(
        `/compliance/workspace/audit/tokens/${v.tokenId}/scope`,
        { frameworkReferences: v.frameworkReferences, controlReferences: v.controlReferences ?? [] },
      );
      return data.data as { control_count: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-audit'] });
      qc.invalidateQueries({ queryKey: ['auditor-tokens'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
    },
  });
}

export function useEvidenceRequests(tokenId?: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-audit-requests', tenantId, tokenId ?? 'all'],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/audit/requests', {
        params: tokenId ? { tokenId } : undefined,
      });
      return data.data as EvidenceRequest[];
    },
    enabled: !!tenantId,
  });
}

/**
 * ⚠ EVERY REQUEST MUTATION INVALIDATES THE ACTIVITY FEED TOO. Raising, fulfilling and withdrawing
 * each write a programme event, so a feed left on a stale cache shows a programme that has already
 * moved on — and it looks entirely correct while doing so.
 */
function useRequestMutation<V>(fn: (v: V) => Promise<EvidenceRequest>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-audit-requests'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
    },
  });
}

export function useRaiseEvidenceRequest() {
  return useRequestMutation(async (v: {
    controlReference: string; title: string;
    tokenId?: string | null; detail?: string | null; dueAt?: string | null;
  }) => {
    const { data } = await apiClient.post('/compliance/workspace/audit/requests', v);
    return data.data as EvidenceRequest;
  });
}

export function useFulfilEvidenceRequest() {
  return useRequestMutation(async (v: { id: string; evidenceId: string }) => {
    const { data } = await apiClient.post(
      `/compliance/workspace/audit/requests/${v.id}/fulfil`, { evidenceId: v.evidenceId });
    return data.data as EvidenceRequest;
  });
}

export function useWithdrawEvidenceRequest() {
  return useRequestMutation(async (v: { id: string; reason: string }) => {
    const { data } = await apiClient.post(
      `/compliance/workspace/audit/requests/${v.id}/withdraw`, { reason: v.reason });
    return data.data as EvidenceRequest;
  });
}
